import React, { useState } from 'react';
import { CalendarIcon, MapPinIcon, ClockIcon, DollarSignIcon, ShoppingCartIcon, EyeIcon, ClipboardListIcon, XIcon, PackageIcon, UserIcon, FilterIcon, CheckCircleIcon, XCircleIcon, AlertTriangleIcon } from 'lucide-react';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { StatsCard } from '../components/ui/StatsCard';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { Visit } from '../types';
import { useSortableData } from '../hooks/useSortableData';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
export function VisitsPage() {
  const {
    user
  } = useAuth();
  const {
    visits,
    users
  } = useApp();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedVisit, setSelectedVisit] = useState<Visit | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 8;
  // Get active vendors for filter (non-admin users)
  const vendors = users.filter(u => u.rolNombre !== 'Administrador' && u.activo && u.permisos?.['new-visit'] === true);
  const vendorOptions = [{
    value: '',
    label: 'Todos los vendedores'
  }, ...vendors.map(v => ({
    value: v.id,
    label: v.nombre
  }))];
  const statusOptions = [{
    value: '',
    label: 'Todos los estados'
  }, {
    value: 'completada',
    label: 'Completada'
  }, {
    value: 'en_progreso',
    label: 'En Progreso'
  }, {
    value: 'cancelada',
    label: 'Cancelada'
  }];
  const typeOptions = [{
    value: '',
    label: 'Todos los tipos'
  }, {
    value: 'cobro',
    label: 'Cobro'
  }, {
    value: 'venta',
    label: 'Venta'
  }, {
    value: 'cobro_y_venta',
    label: 'Cobro y Venta'
  }, {
    value: 'seguimiento',
    label: 'Seguimiento'
  }];
  const filteredVisits = visits.filter(v => isAdmin || v.vendedorId === user?.id).filter(v => {
    if (dateFilter) {
      const visitDate = new Date(v.fecha).toISOString().split('T')[0];
      if (visitDate !== dateFilter) return false;
    }
    if (statusFilter && v.estado !== statusFilter) return false;
    if (vendorFilter && v.vendedorId !== vendorFilter) return false;
    if (typeFilter && v.tipoVisita !== typeFilter) return false;
    return true;
  });
  // Sorting setup
  const {
    items: sortedVisits,
    requestSort,
    sortConfig
  } = useSortableData(filteredVisits, {
    fecha: v => new Date(v.fecha).getTime(),
    vendedor: v => v.vendedorNombre,
    cliente: v => v.clienteNombre,
    tipo: v => v.tipoVisita,
    cobro: v => v.cobro.montoCobrado,
    venta: v => v.venta.montoVenta,
    tiempo: v => v.tiempoTotal || 0,
    estado: v => v.estado
  });
  // Default sort by date descending if no sort config
  const finalVisits = sortConfig ? sortedVisits : [...sortedVisits].sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
  const totalPages = Math.ceil(finalVisits.length / ITEMS_PER_PAGE);
  const paginatedVisits = finalVisits.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  // FIXED: Use Dominican Republic locale and currency
  const formatCurrency = (amount: number) => new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 0
  }).format(amount);
  const formatDate = (date: Date) => new Date(date).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
  const formatTime = (date: Date | null) => date ? new Date(date).toLocaleTimeString('es-DO', {
    hour: '2-digit',
    minute: '2-digit'
  }) : '--:--';
  const getVisitTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      cobro: 'Cobro',
      venta: 'Venta',
      cobro_y_venta: 'Cobro y Venta',
      seguimiento: 'Seguimiento'
    };
    return labels[type] || type;
  };
  const getVisitTypeBadge = (type: string) => {
    const config: Record<string, {
      bg: string;
      text: string;
    }> = {
      cobro: {
        bg: 'bg-emerald-100',
        text: 'text-emerald-700'
      },
      venta: {
        bg: 'bg-blue-100',
        text: 'text-blue-700'
      },
      cobro_y_venta: {
        bg: 'bg-indigo-100',
        text: 'text-indigo-700'
      },
      seguimiento: {
        bg: 'bg-amber-100',
        text: 'text-amber-700'
      }
    };
    const style = config[type] || {
      bg: 'bg-slate-100',
      text: 'text-slate-700'
    };
    return <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold ${style.bg} ${style.text}`} data-id="element-3667">
        {getVisitTypeLabel(type)}
      </span>;
  };
  const getStatusBadge = (status: string) => {
    if (status === 'completada') return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-emerald-100 text-emerald-700" data-id="element-3668">
          <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-3669" />
          Completada
        </span>;
    if (status === 'en_progreso') return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-amber-100 text-amber-700" data-id="element-3670">
          <ClockIcon className="w-3.5 h-3.5" data-id="element-3671" />
          En Progreso
        </span>;
    return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-rose-100 text-rose-700" data-id="element-3672">
        <XCircleIcon className="w-3.5 h-3.5" data-id="element-3673" />
        Cancelada
      </span>;
  };
  const getInvoiceStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pagada_completa: 'Pagada Completa',
      pago_parcial: 'Pago Parcial',
      no_pago: 'No Pagó'
    };
    return labels[status] || status;
  };
  const getPaymentMethodLabel = (method: string | null) => {
    if (!method) return '-';
    const labels: Record<string, string> = {
      efectivo: 'Efectivo',
      transferencia: 'Transferencia',
      cheque: 'Cheque'
    };
    return labels[method] || method;
  };
  // Stats calculations
  const totalCobrado = filteredVisits.reduce((sum, v) => sum + v.cobro.montoCobrado, 0);
  const totalVendido = filteredVisits.reduce((sum, v) => sum + v.venta.montoVenta, 0);
  const visitasCompletadas = filteredVisits.filter(v => v.estado === 'completada').length;
  const visitasConTiempo = filteredVisits.filter(v => v.tiempoTotal);
  const tiempoPromedio = visitasConTiempo.length > 0 ? Math.round(visitasConTiempo.reduce((sum, v) => sum + (v.tiempoTotal || 0), 0) / visitasConTiempo.length) : 0;
  const clearFilters = () => {
    setDateFilter('');
    setStatusFilter('');
    setVendorFilter('');
    setTypeFilter('');
    setCurrentPage(1);
  };
  const hasActiveFilters = dateFilter || statusFilter || vendorFilter || typeFilter;
  // FIXED: Render cobro cell with proper logic
  const renderCobroCell = (visit: Visit) => {
    const tieneCobroTipo = visit.tipoVisita === 'cobro' || visit.tipoVisita === 'cobro_y_venta';
    if (!tieneCobroTipo) {
      return <span className="text-slate-300" data-id="element-3674">—</span>;
    }
    // FIXED: Check both huboCobo AND montoCobrado > 0
    if (!visit.cobro.huboCobo || visit.cobro.montoCobrado <= 0) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-semibold bg-rose-50 text-rose-600" data-id="element-3675">
          No cobró
        </span>;
    }
    return <span className="font-semibold text-emerald-600" data-id="element-3676">
        {formatCurrency(visit.cobro.montoCobrado)}
      </span>;
  };
  // FIXED: Render venta cell with proper logic
  const renderVentaCell = (visit: Visit) => {
    const tieneVentaTipo = visit.tipoVisita === 'venta' || visit.tipoVisita === 'cobro_y_venta';
    if (!tieneVentaTipo) {
      return <span className="text-slate-300" data-id="element-3677">—</span>;
    }
    // FIXED: Check both huboVenta AND montoVenta > 0
    if (!visit.venta.huboVenta || visit.venta.montoVenta <= 0) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-semibold bg-amber-50 text-amber-600" data-id="element-3678">
          No vendió
        </span>;
    }
    return <span className="font-semibold text-indigo-600" data-id="element-3679">
        {formatCurrency(visit.venta.montoVenta)}
      </span>;
  };
  return <div className="space-y-6" data-id="element-3680">
      <PageHeader title="Historial de Visitas" subtitle={`${filteredVisits.length} registros encontrados`} icon={<MapPinIcon className="w-6 h-6" data-id="element-3682" />} data-id="element-3681" />

      {/* Filters */}
      <Card className="animate-stagger-1" data-id="element-3683">
        <div className="flex items-center gap-2 mb-4" data-id="element-3684">
          <FilterIcon className="w-4 h-4 text-slate-400" data-id="element-3685" />
          <span className="text-sm font-semibold text-slate-700" data-id="element-3686">Filtros</span>
          {hasActiveFilters && <span className="text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full font-medium" data-id="element-3687">
              {[dateFilter, statusFilter, vendorFilter, typeFilter].filter(Boolean).length}{' '}
              activos
            </span>}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-id="element-3688">
          <div className="col-span-1 min-w-0" data-id="element-3689">
            <Input type="date" value={dateFilter} onChange={e => {
            setDateFilter(e.target.value);
            setCurrentPage(1);
          }} leftIcon={<CalendarIcon className="w-4 h-4" data-id="element-3691" />} label="Fecha" data-id="element-3690" />
          </div>
          {isAdmin && <div className="col-span-1 min-w-0" data-id="element-3692">
              <Select label="Vendedor" options={vendorOptions} value={vendorFilter} onChange={e => {
            setVendorFilter(e.target.value);
            setCurrentPage(1);
          }} data-id="element-3693" />
            </div>}
          <div className="col-span-1 min-w-0" data-id="element-3694">
            <Select label="Tipo de Visita" options={typeOptions} value={typeFilter} onChange={e => {
            setTypeFilter(e.target.value);
            setCurrentPage(1);
          }} data-id="element-3695" />
          </div>
          <div className="col-span-1 min-w-0" data-id="element-3696">
            <Select label="Estado" options={statusOptions} value={statusFilter} onChange={e => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }} data-id="element-3697" />
          </div>
          <div className="col-span-2 lg:col-span-1 flex items-end" data-id="element-3698">
            <Button variant="secondary" onClick={clearFilters} className="w-full" disabled={!hasActiveFilters} data-id="element-3699">
              Limpiar
            </Button>
          </div>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 animate-stagger-1" data-id="element-3700">
        <StatsCard title="Total Visitas" value={filteredVisits.length} icon={<ClipboardListIcon className="w-5 h-5" data-id="element-3702" />} variant="primary" data-id="element-3701" />
        <StatsCard title="Total Cobrado" value={formatCurrency(totalCobrado)} icon={<DollarSignIcon className="w-5 h-5" data-id="element-3704" />} variant="success" data-id="element-3703" />
        <StatsCard title="Total Vendido" value={formatCurrency(totalVendido)} icon={<ShoppingCartIcon className="w-5 h-5" data-id="element-3706" />} variant="warning" data-id="element-3705" />
        <StatsCard title="Tiempo Promedio" value={`${tiempoPromedio} min`} icon={<ClockIcon className="w-5 h-5" data-id="element-3708" />} variant="default" data-id="element-3707" />
      </div>

      {/* Mobile Card List */}
      <div className="md:hidden space-y-3 animate-stagger-2" data-id="element-3709">
        {filteredVisits.length === 0 ? <Card data-id="element-3710">
            <div className="p-8 text-center" data-id="element-3711">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-slate-100 rounded-2xl mb-3" data-id="element-3712">
                <ClipboardListIcon className="w-7 h-7 text-slate-400" data-id="element-3713" />
              </div>
              <h3 className="text-base font-semibold text-slate-900 mb-1" data-id="element-3714">
                No hay visitas
              </h3>
              <p className="text-slate-500 text-sm" data-id="element-3715">
                {hasActiveFilters ? 'No se encontraron visitas con los filtros aplicados' : 'Aún no tienes visitas registradas'}
              </p>
            </div>
          </Card> : paginatedVisits.map(visit => {
        const tieneCobroTipo = visit.tipoVisita === 'cobro' || visit.tipoVisita === 'cobro_y_venta';
        const tieneVentaTipo = visit.tipoVisita === 'venta' || visit.tipoVisita === 'cobro_y_venta';
        return <Card key={visit.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelectedVisit(visit)} data-id="element-3716">
                <div className="flex items-start justify-between gap-3 mb-3" data-id="element-3717">
                  <div className="flex-1 min-w-0" data-id="element-3718">
                    <p className="font-semibold text-slate-900 truncate" data-id="element-3719">
                      {visit.clienteNombre}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5" data-id="element-3720">
                      {visit.zona}
                    </p>
                    {isAdmin && <p className="text-xs text-indigo-600 font-medium mt-0.5 flex items-center gap-1" data-id="element-3721">
                        <UserIcon className="w-3 h-3" data-id="element-3722" />
                        {visit.vendedorNombre}
                      </p>}
                  </div>
                  <div className="flex flex-col items-end gap-1.5 flex-shrink-0" data-id="element-3723">
                    {getStatusBadge(visit.estado)}
                    {getVisitTypeBadge(visit.tipoVisita)}
                  </div>
                </div>

                <div className="flex items-center gap-2 text-xs text-slate-500 mb-3" data-id="element-3724">
                  <CalendarIcon className="w-3.5 h-3.5" data-id="element-3725" />
                  <span data-id="element-3726">{formatDate(visit.fecha)}</span>
                  <span data-id="element-3727">•</span>
                  <ClockIcon className="w-3.5 h-3.5" data-id="element-3728" />
                  <span data-id="element-3729">{formatTime(visit.horaLlegada)}</span>
                  {visit.tiempoTotal && <>
                      <span data-id="element-3730">•</span>
                      <span data-id="element-3731">{visit.tiempoTotal} min</span>
                    </>}
                </div>

                <div className="grid grid-cols-2 gap-2" data-id="element-3732">
                  {tieneCobroTipo && <div className="p-2.5 bg-emerald-50 rounded-xl" data-id="element-3733">
                      <p className="text-xs text-emerald-600 font-medium mb-0.5" data-id="element-3734">
                        Cobro
                      </p>
                      <p className="font-bold text-emerald-700" data-id="element-3735">
                        {visit.cobro.huboCobo && visit.cobro.montoCobrado > 0 ? formatCurrency(visit.cobro.montoCobrado) : <span className="text-rose-500 text-sm" data-id="element-3736">
                            No cobró
                          </span>}
                      </p>
                    </div>}
                  {tieneVentaTipo && <div className="p-2.5 bg-indigo-50 rounded-xl" data-id="element-3737">
                      <p className="text-xs text-indigo-600 font-medium mb-0.5" data-id="element-3738">
                        Venta
                      </p>
                      <p className="font-bold text-indigo-700" data-id="element-3739">
                        {visit.venta.huboVenta && visit.venta.montoVenta > 0 ? formatCurrency(visit.venta.montoVenta) : <span className="text-amber-500 text-sm" data-id="element-3740">
                            No vendió
                          </span>}
                      </p>
                    </div>}
                </div>

                {(visit as any).productosVencidos !== null && (visit as any).productosVencidos !== undefined && <div className={`mt-3 flex items-center gap-2 text-xs font-medium px-2.5 py-1.5 rounded-lg ${(visit as any).productosVencidos ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'}`} data-id="element-3741">
                      {(visit as any).productosVencidos ? <>
                          <AlertTriangleIcon className="w-3.5 h-3.5" data-id="element-3742" />
                          Tiene productos vencidos
                        </> : <>
                          <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-3743" />
                          Sin productos vencidos
                        </>}
                    </div>}

                {visit.motivoNoPago && <p className="mt-2 text-xs text-rose-600 bg-rose-50 px-2.5 py-1.5 rounded-lg" data-id="element-3744">
                    ⚠ {visit.motivoNoPago}
                  </p>}
              </Card>;
      })}
      </div>

      {/* Desktop Table */}
      <Card padding="none" className="hidden md:block animate-stagger-2" data-id="element-3745">
        {filteredVisits.length === 0 ? <div className="p-16 text-center animate-fade-in" data-id="element-3746">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-3747">
              <ClipboardListIcon className="w-8 h-8 text-slate-400" data-id="element-3748" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-3749">
              No hay visitas
            </h3>
            <p className="text-slate-500 text-sm" data-id="element-3750">
              {hasActiveFilters ? 'No se encontraron visitas con los filtros aplicados' : 'Aún no tienes visitas registradas'}
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-3751">
            <table className="w-full" data-id="element-3752">
              <thead data-id="element-3753">
                <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-3754">
                  <SortableTableHeader label="Fecha" sortKey="fecha" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3755" />
                  {isAdmin && <SortableTableHeader label="Vendedor" sortKey="vendedor" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3756" />}
                  <SortableTableHeader label="Cliente" sortKey="cliente" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3757" />
                  <SortableTableHeader label="Tipo" sortKey="tipo" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3758" />
                  <SortableTableHeader label="Cobro" sortKey="cobro" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3759" />
                  <SortableTableHeader label="Venta" sortKey="venta" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3760" />
                  <SortableTableHeader label="Tiempo" sortKey="tiempo" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3761" />
                  <SortableTableHeader label="Estado" sortKey="estado" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-3762" />
                  <th className="px-5 py-4 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-3763">
                    Vencidos
                  </th>
                  <th className="px-5 py-4 w-12" data-id="element-3764"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-3765">
                {paginatedVisits.map(visit => <tr key={visit.id} className="hover:bg-slate-50/50 transition-colors cursor-pointer" onClick={() => setSelectedVisit(visit)} data-id="element-3766">
                    <td className="px-5 py-4" data-id="element-3767">
                      <p className="font-medium text-slate-900 text-sm" data-id="element-3768">
                        {formatDate(visit.fecha)}
                      </p>
                      <p className="text-xs text-slate-400" data-id="element-3769">
                        {formatTime(visit.horaLlegada)}
                      </p>
                    </td>
                    {isAdmin && <td className="px-5 py-4 text-sm text-slate-700" data-id="element-3770">
                        {visit.vendedorNombre}
                      </td>}
                    <td className="px-5 py-4" data-id="element-3771">
                      <p className="font-medium text-slate-900 text-sm" data-id="element-3772">
                        {visit.clienteNombre}
                      </p>
                      <p className="text-xs text-slate-400" data-id="element-3773">{visit.zona}</p>
                    </td>
                    <td className="px-5 py-4 text-center" data-id="element-3774">
                      {getVisitTypeBadge(visit.tipoVisita)}
                    </td>
                    <td className="px-5 py-4 text-right" data-id="element-3775">
                      {renderCobroCell(visit)}
                    </td>
                    <td className="px-5 py-4 text-right" data-id="element-3776">
                      {renderVentaCell(visit)}
                    </td>
                    <td className="px-5 py-4 text-center text-sm text-slate-600" data-id="element-3777">
                      {visit.tiempoTotal ? `${visit.tiempoTotal} min` : <span className="text-slate-300" data-id="element-3778">—</span>}
                    </td>
                    <td className="px-5 py-4 text-center" data-id="element-3779">
                      {getStatusBadge(visit.estado)}
                    </td>
                    <td className="px-5 py-4 text-center" data-id="element-3780">
                      {(visit as any).productosVencidos === true ? <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-semibold bg-rose-50 text-rose-600" data-id="element-3781">
                          Sí
                        </span> : (visit as any).productosVencidos === false ? <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-600" data-id="element-3782">
                          No
                        </span> : <span className="text-slate-300" data-id="element-3783">—</span>}
                    </td>
                    <td className="px-5 py-4" data-id="element-3784">
                      <Button variant="ghost" size="sm" onClick={e => {
                  e.stopPropagation();
                  setSelectedVisit(visit);
                }} data-id="element-3785">
                        <EyeIcon className="w-4 h-4 text-slate-400" data-id="element-3786" />
                      </Button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </Card>

      {/* Pagination */}
      {filteredVisits.length > 0 && <div className="animate-stagger-3" data-id="element-3787">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredVisits.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-3788" />
        </div>}

      {/* Detail Modal */}
      <Modal isOpen={!!selectedVisit} onClose={() => setSelectedVisit(null)} title="Detalle de Visita" size="lg" data-id="element-3789">
        {selectedVisit && <>
            <ModalBody data-id="element-3790">
              <div className="space-y-6" data-id="element-3791">
                <div className="flex items-start justify-between pb-4 border-b border-slate-100" data-id="element-3792">
                  <div data-id="element-3793">
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-3794">
                      {selectedVisit.clienteNombre}
                    </h3>
                    <p className="text-sm text-slate-500" data-id="element-3795">
                      {selectedVisit.zona}
                    </p>
                  </div>
                  {getStatusBadge(selectedVisit.estado)}
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-3796">
                  <div className="p-4 bg-slate-50 rounded-xl" data-id="element-3797">
                    <p className="text-xs text-slate-500 mb-1" data-id="element-3798">Vendedor</p>
                    <p className="font-semibold text-slate-900" data-id="element-3799">
                      {selectedVisit.vendedorNombre}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl" data-id="element-3800">
                    <p className="text-xs text-slate-500 mb-1" data-id="element-3801">
                      Tipo de Visita
                    </p>
                    <p className="font-semibold text-slate-900" data-id="element-3802">
                      {getVisitTypeLabel(selectedVisit.tipoVisita)}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl" data-id="element-3803">
                    <p className="text-xs text-slate-500 mb-1" data-id="element-3804">Fecha</p>
                    <p className="font-semibold text-slate-900" data-id="element-3805">
                      {formatDate(selectedVisit.fecha)}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl" data-id="element-3806">
                    <p className="text-xs text-slate-500 mb-1" data-id="element-3807">Horario</p>
                    <p className="font-semibold text-slate-900" data-id="element-3808">
                      {formatTime(selectedVisit.horaLlegada)} -{' '}
                      {formatTime(selectedVisit.horaSalida)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-6 p-4 bg-[#eef2fc] rounded-xl" data-id="element-3809">
                  <div className="flex items-center gap-2" data-id="element-3810">
                    <ClockIcon className="w-5 h-5 text-[#2952C4]" data-id="element-3811" />
                    <div data-id="element-3812">
                      <p className="text-xs text-[#2952C4] font-medium" data-id="element-3813">
                        Tiempo Total
                      </p>
                      <p className="font-bold text-[#0D1B46]" data-id="element-3814">
                        {selectedVisit.tiempoTotal || 0} minutos
                      </p>
                    </div>
                  </div>
                  {selectedVisit.ubicacionLlegada && <div className="flex items-center gap-2" data-id="element-3815">
                      <MapPinIcon className="w-5 h-5 text-[#2952C4]" data-id="element-3816" />
                      <div data-id="element-3817">
                        <p className="text-xs text-[#2952C4] font-medium" data-id="element-3818">
                          Ubicación GPS
                        </p>
                        <p className="font-bold text-[#0D1B46]" data-id="element-3819">
                          {selectedVisit.ubicacionLlegada.lat.toFixed(4)},{' '}
                          {selectedVisit.ubicacionLlegada.lng.toFixed(4)}
                        </p>
                      </div>
                    </div>}
                </div>

                {(selectedVisit.tipoVisita === 'cobro' || selectedVisit.tipoVisita === 'cobro_y_venta') && <div className={`p-4 rounded-xl border ${selectedVisit.cobro.huboCobo && selectedVisit.cobro.montoCobrado > 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`} data-id="element-3820">
                    <h4 className={`font-semibold mb-3 flex items-center gap-2 ${selectedVisit.cobro.huboCobo && selectedVisit.cobro.montoCobrado > 0 ? 'text-emerald-800' : 'text-rose-800'}`} data-id="element-3821">
                      <DollarSignIcon className="w-5 h-5" data-id="element-3822" />
                      {selectedVisit.cobro.huboCobo && selectedVisit.cobro.montoCobrado > 0 ? 'Cobro Realizado' : 'Sin Cobro'}
                    </h4>
                    {selectedVisit.cobro.huboCobo && selectedVisit.cobro.montoCobrado > 0 ? <div className="space-y-3" data-id="element-3823">
                        <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-3824">
                          <div data-id="element-3825">
                            <p className="text-emerald-600" data-id="element-3826">Monto Cobrado</p>
                            <p className="font-bold text-emerald-900" data-id="element-3827">
                              {formatCurrency(selectedVisit.cobro.montoCobrado)}
                            </p>
                          </div>
                          <div data-id="element-3828">
                            <p className="text-emerald-600" data-id="element-3829">Método de Pago</p>
                            <p className="font-bold text-emerald-900" data-id="element-3830">
                              {getPaymentMethodLabel(selectedVisit.cobro.metodoPago)}
                            </p>
                          </div>
                          <div data-id="element-3831">
                            <p className="text-emerald-600" data-id="element-3832">Factura</p>
                            <p className="font-bold text-emerald-900" data-id="element-3833">
                              {selectedVisit.cobro.numeroFactura || '-'}
                            </p>
                          </div>
                          <div data-id="element-3834">
                            <p className="text-emerald-600" data-id="element-3835">Estado</p>
                            <p className="font-bold text-emerald-900" data-id="element-3836">
                              {getInvoiceStatusLabel(selectedVisit.cobro.estadoFactura)}
                            </p>
                          </div>
                        </div>
                        {selectedVisit.cobro.metodoPago === 'cheque' && selectedVisit.cobro.chequeImagen && <div className="mt-3" data-id="element-3837">
                              <p className="text-sm font-semibold text-emerald-700 mb-2" data-id="element-3838">
                                Imagen del Cheque
                              </p>
                              <img src={selectedVisit.cobro.chequeImagen} alt="Cheque adjunto" className="w-full max-h-48 object-contain rounded-xl border border-emerald-200 bg-white" data-id="element-3839" />
                            </div>}
                      </div> : <div data-id="element-3840">
                        <p className="text-sm text-rose-700 font-medium" data-id="element-3841">
                          El cliente no realizó pago
                        </p>
                        {selectedVisit.motivoNoPago && <p className="mt-2 text-sm text-rose-600 bg-rose-100 p-2 rounded-lg" data-id="element-3842">
                            Motivo: {selectedVisit.motivoNoPago}
                          </p>}
                      </div>}
                  </div>}

                {(selectedVisit.tipoVisita === 'venta' || selectedVisit.tipoVisita === 'cobro_y_venta') && <div className={`p-4 rounded-xl border ${selectedVisit.venta.huboVenta && selectedVisit.venta.montoVenta > 0 ? 'bg-[#eef2fc] border-[#d5e0f7]' : 'bg-amber-50 border-amber-200'}`} data-id="element-3843">
                    <h4 className={`font-semibold mb-3 flex items-center gap-2 ${selectedVisit.venta.huboVenta && selectedVisit.venta.montoVenta > 0 ? 'text-[#1E3D96]' : 'text-amber-800'}`} data-id="element-3844">
                      <ShoppingCartIcon className="w-5 h-5" data-id="element-3845" />
                      {selectedVisit.venta.huboVenta && selectedVisit.venta.montoVenta > 0 ? 'Venta Realizada' : 'Sin Venta'}
                    </h4>
                    {selectedVisit.venta.huboVenta && selectedVisit.venta.montoVenta > 0 ? <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-3846">
                        <div data-id="element-3847">
                          <p className="text-[#2952C4]" data-id="element-3848">Monto de Venta</p>
                          <p className="font-bold text-[#0D1B46]" data-id="element-3849">
                            {formatCurrency(selectedVisit.venta.montoVenta)}
                          </p>
                        </div>
                        <div data-id="element-3850">
                          <p className="text-[#2952C4]" data-id="element-3851">Número de Pedido</p>
                          <p className="font-bold text-[#0D1B46]" data-id="element-3852">
                            {selectedVisit.venta.numeroPedido || '-'}
                          </p>
                        </div>
                      </div> : <p className="text-sm text-amber-700 font-medium" data-id="element-3853">
                        No se realizó venta en esta visita
                      </p>}
                  </div>}

                {selectedVisit.comentarios && <div data-id="element-3854">
                    <p className="text-sm font-medium text-slate-700 mb-2" data-id="element-3855">
                      Comentarios
                    </p>
                    <p className="text-slate-600 bg-slate-50 p-3 rounded-xl text-sm" data-id="element-3856">
                      {selectedVisit.comentarios}
                    </p>
                  </div>}

                {(selectedVisit as any).productosVencidos !== null && (selectedVisit as any).productosVencidos !== undefined && <div className={`p-4 rounded-xl border flex items-center gap-3 ${(selectedVisit as any).productosVencidos ? 'bg-rose-50 border-rose-200' : 'bg-emerald-50 border-emerald-200'}`} data-id="element-3857">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${(selectedVisit as any).productosVencidos ? 'bg-rose-100' : 'bg-emerald-100'}`} data-id="element-3858">
                        <span className="text-base" data-id="element-3859">
                          {(selectedVisit as any).productosVencidos ? '⚠️' : '✅'}
                        </span>
                      </div>
                      <div data-id="element-3860">
                        <p className={`text-xs font-semibold uppercase tracking-wide mb-0.5 ${(selectedVisit as any).productosVencidos ? 'text-rose-500' : 'text-emerald-500'}`} data-id="element-3861">
                          Productos Vencidos
                        </p>
                        <p className={`font-bold text-sm ${(selectedVisit as any).productosVencidos ? 'text-rose-800' : 'text-emerald-800'}`} data-id="element-3862">
                          {(selectedVisit as any).productosVencidos ? 'El cliente tiene productos vencidos' : 'Sin productos vencidos'}
                        </p>
                      </div>
                    </div>}

                {/* Devolucion Items */}
                {selectedVisit.devolucionItems && selectedVisit.devolucionItems.length > 0 && <div className="p-4 rounded-xl border border-rose-200 bg-rose-50" data-id="element-3863">
                      <h4 className="font-semibold mb-3 flex items-center gap-2 text-rose-800" data-id="element-3864">
                        <PackageIcon className="w-5 h-5" data-id="element-3865" />
                        Productos para Devolución (
                        {selectedVisit.devolucionItems.length})
                      </h4>
                      <div className="space-y-2" data-id="element-3866">
                        {selectedVisit.devolucionItems.map((item, idx) => <div key={idx} className="p-3 bg-white rounded-lg border border-rose-100" data-id="element-3867">
                            <div className="flex items-start justify-between gap-2 mb-2" data-id="element-3868">
                              <div data-id="element-3869">
                                <p className="font-semibold text-slate-900 text-sm" data-id="element-3870">
                                  {item.productoNombre}
                                </p>
                                <p className="text-xs text-slate-500 font-mono" data-id="element-3871">
                                  {item.productoSku}
                                </p>
                              </div>
                            </div>
                            <div className="grid grid-cols-4 gap-3 text-sm" data-id="element-3872">
                              <div data-id="element-3873">
                                <p className="text-rose-600 text-xs" data-id="element-3874">
                                  Cantidad
                                </p>
                                <p className="font-bold text-slate-900" data-id="element-3875">
                                  {item.cantidad}
                                </p>
                              </div>
                              <div data-id="element-3876">
                                <p className="text-rose-600 text-xs" data-id="element-3877">Precio</p>
                                <p className="font-bold text-slate-900" data-id="element-3878">
                                  {item.precioUnitario ? formatCurrency(item.precioUnitario) : '-'}
                                </p>
                              </div>
                              <div data-id="element-3879">
                                <p className="text-rose-600 text-xs" data-id="element-3880">Lote</p>
                                <p className="font-bold text-slate-900" data-id="element-3881">
                                  {item.lote || '-'}
                                </p>
                              </div>
                              <div data-id="element-3882">
                                <p className="text-rose-600 text-xs" data-id="element-3883">
                                  F. Vencimiento
                                </p>
                                <p className="font-bold text-slate-900" data-id="element-3884">
                                  {item.fechaVencimiento ? new Date(item.fechaVencimiento).toLocaleDateString('es-DO', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        }) : '-'}
                                </p>
                              </div>
                            </div>
                          </div>)}
                      </div>
                    </div>}

                {selectedVisit.proximaVisita && <div className="p-3 bg-amber-50 rounded-xl border border-amber-200" data-id="element-3885">
                    <p className="text-sm text-amber-700" data-id="element-3886">
                      <span className="font-semibold" data-id="element-3887">Próxima visita:</span>{' '}
                      {formatDate(selectedVisit.proximaVisita)}
                    </p>
                  </div>}
              </div>
            </ModalBody>
            <ModalFooter data-id="element-3888">
              <Button variant="secondary" onClick={() => setSelectedVisit(null)} data-id="element-3889">
                Cerrar
              </Button>
            </ModalFooter>
          </>}
      </Modal>
    </div>;
}